import React from "react";
import { Button, Checkbox, DatePicker, Radio, Select, Tooltip } from "antd";
import { PiExport } from "react-icons/pi";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import * as TYPES from "@src/redux/types";
import ReportLevelFilter from "./ReportLevelFilter";

const { RangePicker } = DatePicker;

interface ReportFilterBarProps {
  rootLevelList: TYPES.ReportRootLevel[];
  rootLevelOrganizationId?: string;
  fromDate?: string;
  toDate?: string;
  excludeEmptyAnswers: boolean;
  readingScope: TYPES.ReportReadingScope;
  onRootLevelChange: (rootLevelOrganizationId?: string) => void;
  onDateRangeChange: (fromDate?: string, toDate?: string) => void;
  onExcludeEmptyAnswersChange: (excludeEmptyAnswers: boolean) => void;
  onReadingScopeChange: (readingScope: TYPES.ReportReadingScope) => void;
  onOrganizationChange: (organizationId?: string) => void;
  onExportClick: () => void;
  exportDisabled?: boolean;
}

const ReportFilterBar: React.FC<ReportFilterBarProps> = ({
  rootLevelList,
  rootLevelOrganizationId,
  fromDate,
  toDate,
  excludeEmptyAnswers,
  readingScope,
  onRootLevelChange,
  onDateRangeChange,
  onExcludeEmptyAnswersChange,
  onReadingScopeChange,
  onOrganizationChange,
  onExportClick,
  exportDisabled,
}) => {
  const { t } = useTranslation();

  const rangeValue: [Dayjs, Dayjs] | undefined =
    fromDate && toDate ? [dayjs(fromDate), dayjs(toDate)] : undefined;

  const handleRangeChange = (dates: any) => {
    if (!dates || !dates[0] || !dates[1]) {
      onDateRangeChange(undefined, undefined);
      return;
    }
    // Sent as plain dates: the backend widens them to start/end of day in its
    // own timezone, so shipping an ISO instant here would shift the boundary.
    onDateRangeChange(
      dates[0].format("YYYY-MM-DD"),
      dates[1].format("YYYY-MM-DD")
    );
  };

  return (
    <div className="report-filter-bar">
      <div className="report-filter-field">
        <label className="report-filter-label">
          {t("date_range") || "Date Range"}
        </label>
        <RangePicker
          className="report-range-picker"
          value={rangeValue as any}
          onChange={handleRangeChange}
          format="DD-MMM-YYYY"
          allowClear
        />
      </div>

      <div className="report-filter-field">
        <label className="report-filter-label">
          {t("root_level") || "Root Level"}
        </label>
        <Select
          className="report-root-level-select"
          value={rootLevelOrganizationId}
          onChange={onRootLevelChange}
          placeholder={t("select_root_level") || "Select Root Level"}
          options={rootLevelList.map((x) => ({
            label: x.levelName ? `${x.name} (${x.levelName})` : x.name,
            value: x.id,
          }))}
          showSearch
          optionFilterProp="label"
          allowClear
        />
      </div>

      <ReportLevelFilter
        rootLevelOrganizationId={rootLevelOrganizationId}
        onOrganizationChange={onOrganizationChange}
      />

      <div className="report-filter-field">
        <label className="report-filter-label">
          {t("readings") || "Readings"}
        </label>
        <Radio.Group
          className="report-reading-scope"
          value={readingScope}
          onChange={(e) => onReadingScopeChange(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="all">
            {t("all_readings") || "All Readings"}
          </Radio.Button>
          <Radio.Button value="last">
            {t("last_reading") || "Last Reading"}
          </Radio.Button>
        </Radio.Group>
      </div>

      <div className="report-filter-field report-filter-toggle">
        <Checkbox
          checked={excludeEmptyAnswers}
          onChange={(e) => onExcludeEmptyAnswersChange(e.target.checked)}
        >
          {t("exclude_empty_answers") || "Exclude blank / “Not at all”"}
        </Checkbox>
      </div>

      <div className="report-filter-actions">
        <Tooltip title={t("export") || "Export"}>
          <Button
            type="primary"
            icon={<PiExport size={18} />}
            onClick={onExportClick}
            disabled={exportDisabled}
            className="report-export-button"
          >
            {t("export") || "Export"}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};

export default ReportFilterBar;
